/**
 * Превью вложения: blob URL, миниатюры карточек, lazy fetch.
 */
import type { FileItem } from '@/common/types/fileItem'
import {
  blobFromPreviewField,
  coerceBlobMimeForPreview,
  isPdfFileName,
  isRasterFileName,
  resolveAttachmentPreviewKind,
} from '@/common/utils/attachmentPreviewUtils'
import { attachmentUiRowKey } from '@/common/utils/fileAttachmentsUniData'
import { computed, onUnmounted, ref, watch, type Ref } from 'vue'

export type AttachmentPreviewFileMeta = {
  getFileName: (file: FileItem) => string
  hasNativeFile: (file: FileItem) => file is FileItem & { file: File }
}

export type UseAttachmentPreviewOptions = AttachmentPreviewFileMeta & {
  fileToPreview: Ref<FileItem | null>
  showLargePreview: Ref<boolean>
  fetchAttachmentBlob?: (file: FileItem) => Promise<Blob | null>
}

export function useAttachmentPreview(options: UseAttachmentPreviewOptions) {
  const previewDisplayUrl = ref<string | null>(null)
  const previewLoading = ref(false)
  const previewError = ref<string | null>(null)
  const thumbUrlByKey = ref<Record<string, string>>({})
  let previewLoadGen = 0

  function revokeObjectUrl(url: string | null | undefined): void {
    if (url) URL.revokeObjectURL(url)
  }

  function revokePreviewDisplayUrl(): void {
    revokeObjectUrl(previewDisplayUrl.value)
    previewDisplayUrl.value = null
  }

  function clearThumbCache(): void {
    for (const url of Object.values(thumbUrlByKey.value)) {
      revokeObjectUrl(url)
    }
    thumbUrlByKey.value = {}
  }

  function getCardThumbUrl(file: FileItem): string | null {
    const key = attachmentUiRowKey(file)
    if (thumbUrlByKey.value[key]) return thumbUrlByKey.value[key]
    const blob = blobFromPreviewField(
      file.fields as Record<string, unknown>,
      options.getFileName(file),
    )
    if (!blob) return null
    const url = URL.createObjectURL(blob)
    thumbUrlByKey.value = { ...thumbUrlByKey.value, [key]: url }
    return url
  }

  async function resolvePreviewObjectUrl(file: FileItem): Promise<string | null> {
    if (options.hasNativeFile(file)) {
      const name = options.getFileName(file)
      const mime = file.file.type?.toLowerCase() ?? ''
      if (
        isRasterFileName(name) ||
        isPdfFileName(name) ||
        mime.startsWith('image/') ||
        mime.includes('pdf')
      ) {
        return URL.createObjectURL(file.file)
      }
      return null
    }

    const inline = blobFromPreviewField(
      file.fields as Record<string, unknown>,
      options.getFileName(file),
    )
    if (inline) {
      return URL.createObjectURL(inline)
    }

    const name = options.getFileName(file)
    if (!options.fetchAttachmentBlob) return null
    if (!isRasterFileName(name) && !isPdfFileName(name)) return null

    const blob = await options.fetchAttachmentBlob(file)
    if (!blob) return null
    return URL.createObjectURL(coerceBlobMimeForPreview(blob, name))
  }

  async function loadPreviewForFile(file: FileItem | null): Promise<void> {
    const gen = ++previewLoadGen
    revokePreviewDisplayUrl()
    previewError.value = null
    if (!file || !options.showLargePreview.value) {
      previewLoading.value = false
      return
    }

    const kind = resolveAttachmentPreviewKind(options.getFileName(file))
    if (kind === 'placeholder' || kind === 'office') {
      previewLoading.value = false
      return
    }

    previewLoading.value = true
    try {
      const url = await resolvePreviewObjectUrl(file)
      if (gen !== previewLoadGen) {
        revokeObjectUrl(url)
        return
      }
      previewDisplayUrl.value = url
    } catch {
      if (gen === previewLoadGen) {
        previewError.value = 'Не удалось загрузить превью'
      }
    } finally {
      if (gen === previewLoadGen) previewLoading.value = false
    }
  }

  const previewKind = computed(() => {
    if (!options.fileToPreview.value) return 'none' as const
    return resolveAttachmentPreviewKind(options.getFileName(options.fileToPreview.value))
  })

  watch(
    () => {
      const file = options.fileToPreview.value
      if (!file) return null
      return attachmentUiRowKey(file)
    },
    (rowKey, prevRowKey) => {
      if (!rowKey) {
        void loadPreviewForFile(null)
        return
      }
      if (rowKey === prevRowKey && previewDisplayUrl.value && !previewError.value) {
        return
      }
      void loadPreviewForFile(options.fileToPreview.value)
    },
    { immediate: true },
  )

  onUnmounted(() => {
    revokePreviewDisplayUrl()
    clearThumbCache()
  })

  return {
    previewDisplayUrl,
    previewLoading,
    previewError,
    previewKind,
    getCardThumbUrl,
    clearThumbCache,
    loadPreviewForFile,
  }
}
